import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Select, Input, InputNumber, Tag, Tooltip } from 'antd';
import { ExclamationCircleFilled, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import useCPTCodeOptions from 'hooks/useCPTCodeOptions';
import { TBillingCodeConfig, TCPTCode } from 'types/insuranceBilling';
import s from './s.module.less';

type TBillingCodeEntry = {
    id: number;
    cptCode?: string;
    description: string;
    insuranceRate?: number;
    isCustom: boolean;
};

const MAX_CODES = 6;

const CPT_CODE_TOOLTIP =
    'The CPT billing code for this service. For time based codes, the right code depends on the time you actually spend with the patient, not the scheduled length. This is a starting point, you confirm the final code when you submit each claim.';

const INSURANCE_RATE_TOOLTIP =
    'What you charge for this service. This is the amount Klarity puts on the claim, not what you get paid. Payers reimburse at your contracted rate and never pay more than you bill, so set this at or above that rate.';

let nextId = -1;

const ADD_NEW_SENTINEL = '__ADD_NEW__';

type CPTCodeSelectProps = {
    value?: string;
    options: TBillingCodeConfig[];
    onSelect: (value: string) => void;
    onAddCustom: (searchValue: string) => void;
};

const CPTCodeSelect = ({ value, options, onSelect, onAddCustom }: CPTCodeSelectProps) => {
    const [searchValue, setSearchValue] = useState('');
    const [customCodes, setCustomCodes] = useState<string[]>([]);

    const allOptions = React.useMemo(() => {
        const items: { label: React.ReactNode; value: string }[] = [];
        const addedValues = new Set<string>();
        if (value && !options.some((opt) => opt.code === value) && !customCodes.includes(value)) {
            items.push({ label: value, value });
            addedValues.add(value);
        }
        customCodes.forEach((code) => {
            if (!options.some((opt) => opt.code === code) && !addedValues.has(code)) {
                items.push({ label: code, value: code });
                addedValues.add(code);
            }
        });
        options.forEach((opt) => {
            items.push({ label: `${opt.code} — ${opt.name}`, value: opt.code });
        });
        return items;
    }, [value, customCodes, options]);

    const displayOptions = React.useMemo(() => {
        if (!searchValue) return allOptions;
        const search = searchValue.toLowerCase();
        const filtered = allOptions.filter((opt) => String(opt.label).toLowerCase().includes(search));
        if (filtered.length === 0) {
            return [
                {
                    label: (
                        <span>
                            Code not found. <span className={s.addNewLink}>Add new code</span>
                        </span>
                    ),
                    value: ADD_NEW_SENTINEL,
                },
            ];
        }
        return filtered;
    }, [allOptions, searchValue]);

    const handleSelect = (val: string) => {
        if (val === ADD_NEW_SENTINEL) {
            const trimmed = searchValue.trim();
            if (trimmed) {
                setCustomCodes((prev) => [...prev, trimmed]);
                onAddCustom(trimmed);
            }
        } else {
            onSelect(val);
        }
        setSearchValue('');
    };

    return (
        <Select
            showSearch
            value={value}
            placeholder="Search or enter a CPT code"
            searchValue={searchValue}
            onSearch={setSearchValue}
            onSelect={handleSelect}
            filterOption={false}
            className={s.cptSelect}
            popupClassName={s.cptDropdown}
            notFoundContent={null}
            options={displayOptions}
        />
    );
};

type BillingCodesAndRatesProps = {
    initialValue?: TCPTCode[];
    onChange?: (cpts: TCPTCode[]) => void;
    specialty?: string;
    isOps?: boolean;
    providerId?: number;
    autoSetInsuranceRate?: boolean;
    defaultInsuranceRate?: number;
};

const cptToEntry = (cpt: TCPTCode): TBillingCodeEntry => ({
    id: nextId--,
    cptCode: cpt.cptCode,
    description: cpt.description,
    insuranceRate: cpt.insuranceRate ?? undefined,
    isCustom: cpt.isCustom ?? false,
});

const entriesToCptCodes = (entries: TBillingCodeEntry[]): TCPTCode[] => {
    return entries
        .filter((e) => e.cptCode !== undefined)
        .map((e, index) => ({
            cptCode: e.cptCode!,
            description: e.description,
            insuranceRate: e.insuranceRate ?? 0,
            isPrimary: index === 0,
            isCustom: e.isCustom,
            seq: index,
        }));
};

const BillingCodesAndRates = ({
    initialValue,
    onChange,
    specialty,
    isOps,
    providerId,
    autoSetInsuranceRate,
    defaultInsuranceRate,
}: BillingCodesAndRatesProps) => {
    const [cptOptions] = useCPTCodeOptions(specialty, isOps, providerId);
    const manuallyEditedRates = useRef<Set<number>>(new Set());
    const [entries, setEntries] = useState<TBillingCodeEntry[]>(() => {
        if (initialValue && initialValue.length > 0) {
            const initial = initialValue.map(cptToEntry);
            if (autoSetInsuranceRate) {
                initial.forEach((entry) => {
                    if (entry.insuranceRate !== undefined && entry.insuranceRate > 0) {
                        manuallyEditedRates.current.add(entry.id);
                    }
                });
            }
            return initial;
        }
        return [{ id: nextId--, description: '', isCustom: false }];
    });

    const fireChange = useCallback(
        (newEntries: TBillingCodeEntry[]) => {
            onChange?.(entriesToCptCodes(newEntries));
        },
        [onChange],
    );

    useEffect(() => {
        if (!autoSetInsuranceRate || !defaultInsuranceRate || defaultInsuranceRate <= 0) return;

        setEntries((prev) => {
            let changed = false;
            const next = prev.map((entry) => {
                if (!manuallyEditedRates.current.has(entry.id) && entry.insuranceRate !== defaultInsuranceRate) {
                    changed = true;
                    return { ...entry, insuranceRate: defaultInsuranceRate };
                }
                return entry;
            });
            if (changed) {
                fireChange(next);
                return next;
            }
            return prev;
        });
    }, [autoSetInsuranceRate, defaultInsuranceRate, fireChange]);

    const updateEntry = useCallback(
        (id: number, updates: Partial<TBillingCodeEntry>) => {
            setEntries((prev) => {
                const next = prev.map((entry) => (entry.id === id ? { ...entry, ...updates } : entry));
                fireChange(next);
                return next;
            });
        },
        [fireChange],
    );

    const handleSelectCode = useCallback(
        (id: number, value: string) => {
            const matched = cptOptions.find((opt) => opt.code === value);
            if (matched) {
                updateEntry(id, {
                    cptCode: matched.code,
                    description: matched.name,
                    isCustom: false,
                });
            }
        },
        [cptOptions, updateEntry],
    );

    const handleAddCustomCode = useCallback(
        (id: number, searchValue: string) => {
            const trimmed = searchValue.trim();
            if (trimmed) {
                updateEntry(id, {
                    cptCode: trimmed,
                    description: '',
                    isCustom: true,
                });
            }
        },
        [updateEntry],
    );

    const handleAddCode = useCallback(() => {
        if (entries.length >= MAX_CODES) return;
        setEntries((prev) => {
            const next = [
                ...prev,
                {
                    id: nextId--,
                    description: '',
                    isCustom: false,
                    insuranceRate:
                        autoSetInsuranceRate && defaultInsuranceRate && defaultInsuranceRate > 0
                            ? defaultInsuranceRate
                            : undefined,
                },
            ];
            fireChange(next);
            return next;
        });
    }, [entries.length, fireChange, autoSetInsuranceRate, defaultInsuranceRate]);

    const handleRemoveCode = useCallback(
        (id: number) => {
            manuallyEditedRates.current.delete(id);
            setEntries((prev) => {
                const next = prev.filter((entry) => entry.id !== id);
                fireChange(next);
                return next;
            });
        },
        [fireChange],
    );

    const renderCodeEntry = (entry: TBillingCodeEntry, index: number) => {
        const isPrimary = index === 0;
        const selectedCodes = entries.filter((e) => e.id !== entry.id && e.cptCode).map((e) => e.cptCode);

        const filteredOptions = cptOptions.filter((opt) => !selectedCodes.includes(opt.code));

        return (
            <div key={entry.id} className={s.codeCard}>
                <div className={s.cardHeader}>
                    <Tag className={isPrimary ? s.primaryTag : s.addOnTag}>{isPrimary ? 'Primary' : 'Add-on'}</Tag>
                    {!isPrimary && (
                        <span className={s.removeBtn} onClick={() => handleRemoveCode(entry.id)}>
                            Remove
                        </span>
                    )}
                </div>
                <div className={s.cardBody}>
                    <div className={s.fieldGroup}>
                        <div className={s.cptField}>
                            <div className={s.fieldLabel}>
                                CPT code
                                <Tooltip title={CPT_CODE_TOOLTIP}>
                                    <QuestionCircleOutlined className={s.tooltipIcon} />
                                </Tooltip>
                            </div>
                            <CPTCodeSelect
                                value={entry.cptCode}
                                options={filteredOptions}
                                onSelect={(value) => handleSelectCode(entry.id, value)}
                                onAddCustom={(searchValue) => handleAddCustomCode(entry.id, searchValue)}
                            />
                        </div>
                        <div className={s.descField}>
                            <div className={s.fieldLabel}>Description</div>
                            <Input
                                value={entry.description}
                                onChange={(e) => updateEntry(entry.id, { description: e.target.value })}
                                placeholder=""
                            />
                        </div>
                        <div className={s.rateField}>
                            <div className={s.fieldLabel}>
                                Insurance rate
                                <Tooltip title={INSURANCE_RATE_TOOLTIP}>
                                    <QuestionCircleOutlined className={s.tooltipIcon} />
                                </Tooltip>
                            </div>
                            <InputNumber
                                value={entry.insuranceRate}
                                onChange={(value) => {
                                    if (autoSetInsuranceRate) {
                                        manuallyEditedRates.current.add(entry.id);
                                    }
                                    updateEntry(entry.id, { insuranceRate: value ?? undefined });
                                }}
                                precision={2}
                                prefix="$"
                                controls={false}
                                status={
                                    entry.insuranceRate !== undefined && entry.insuranceRate <= 0 ? 'error' : undefined
                                }
                                suffix={
                                    entry.insuranceRate !== undefined && entry.insuranceRate <= 0 ? (
                                        <ExclamationCircleFilled className={s.errorIcon} />
                                    ) : undefined
                                }
                                className={s.rateInput}
                            />
                            {entry.insuranceRate !== undefined && entry.insuranceRate <= 0 && (
                                <div className={s.errorText}>Amount must be greater than $0.00</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={s.wrap}>
            {entries.map((entry, index) => renderCodeEntry(entry, index))}
            <span
                className={`${s.addCodeBtn} ${entries.length >= MAX_CODES ? s.disabled : ''}`}
                onClick={handleAddCode}
            >
                <PlusOutlined /> Add code
            </span>
        </div>
    );
};

export default BillingCodesAndRates;
